/**
 * ProductManualContext.jsx
 *
 * Shared in-memory state for "manual" cells in Summary_Products and
 * Summary_Communication. Both pages show the same 44-column table, so the
 * CN code / CN name / product name / material composition entered on one page
 * must be visible on the other.
 *
 * State shape:
 *   {
 *     [rowKey]: {
 *       [colN]: string
 *     }
 *   }
 *
 * Example after user types in Summary_Products:
 *   {
 *     "proc-P1":         { 3: "7208 10 00", 4: "Flat-rolled products...", 5: "HRC-Grade-A" },
 *     "proc-P1__v-123":  { 3: "7208 25 00", 4: "Hot-rolled, pickled...", 5: "HRC-Grade-B" }
 *   }
 *
 * NOTE: This is in-memory only, lives for the session. Refreshing the browser
 * clears it. Firestore persistence will be added later in a separate change
 * that covers ALL 22+ manual fields at once, not just CN codes.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const ProductManualContext = createContext(null);

export const ProductManualProvider = ({ children }) => {
  const [data, setData] = useState({});

  /** Update a single cell. */
  const setCell = useCallback((rowKey, colN, value) => {
    setData((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), [colN]: value },
    }));
  }, []);

  /**
   * Update multiple cells in one row at once.
   * Used when CN code selection auto-fills CN name (col 3 + col 4 together).
   */
  const setCells = useCallback((rowKey, updates) => {
    setData((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), ...updates },
    }));
  }, []);

  /** Read one cell — convenience getter. */
  const getCell = useCallback(
    (rowKey, colN) => data[rowKey]?.[colN] ?? '',
    [data]
  );

  /** Read all cells for a row — used to apply EXAMPLE_MANUAL overrides. */
  const getRow = useCallback((rowKey) => data[rowKey] || {}, [data]);

  /** Clear one row entirely (e.g. when a variant is removed). */
  const clearRow = useCallback((rowKey) => {
    setData((prev) => {
      const { [rowKey]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const value = { data, setCell, setCells, getCell, getRow, clearRow };

  return (
    <ProductManualContext.Provider value={value}>
      {children}
    </ProductManualContext.Provider>
  );
};

/**
 * Hook for consumers. Falls back to a no-op shim if the provider is missing
 * so existing screens don't crash before App.js is updated.
 */
export const useProductManual = () => {
  const ctx = useContext(ProductManualContext);
  if (!ctx) {
    // Soft fallback — log once and return safe no-ops
    if (typeof window !== 'undefined' && !window.__warnedNoProductManual) {
      // eslint-disable-next-line no-console
      console.warn(
        '[ProductManualContext] No provider in tree — wrap your app in <ProductManualProvider>. ' +
          'Falling back to in-component state (CN codes will not sync between summary pages).'
      );
      window.__warnedNoProductManual = true;
    }
    return {
      data: {},
      setCell: () => {},
      setCells: () => {},
      getCell: () => '',
      getRow: () => ({}),
      clearRow: () => {},
    };
  }
  return ctx;
};